import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateCampoTipoAtivo } from '@/hooks/useCamposTipoAtivo';
import { CampoTipoAtivoFormData, TipoCampo, TabAtivo } from '@/types/tipo-ativo';

const campoSchema = z.object({
  nome_campo: z.string().min(1, 'Nome do campo é obrigatório'),
  label: z.string().min(1, 'Label é obrigatório'),
  tipo_campo: z.string().min(1, 'Tipo é obrigatório'),
  descricao: z.string().optional(),
  obrigatorio: z.boolean().optional(),
  ordem: z.number().optional(),
  tab: z.string().optional(),
  configuracoes: z.object({
    placeholder: z.string().optional(),
    opcoes: z.array(z.string()).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    sufixo: z.string().optional(),
    prefixo: z.string().optional(),
  }).optional(),
});

interface NewCampoTipoAtivoDialogProps {
  tipoAtivoId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NewCampoTipoAtivoDialog = ({ tipoAtivoId, open, onOpenChange }: NewCampoTipoAtivoDialogProps) => {
  const createMutation = useCreateCampoTipoAtivo();

  const form = useForm<CampoTipoAtivoFormData>({
    resolver: zodResolver(campoSchema),
    defaultValues: {
      tipo_ativo_id: tipoAtivoId,
      nome_campo: '',
      label: '',
      tipo_campo: 'text',
      descricao: '',
      obrigatorio: false,
      ordem: 0,
      tab: 'tecnico',
      configuracoes: {},
    },
  });

  const tipoCampoSelecionado = form.watch('tipo_campo');

  const onSubmit = async (data: CampoTipoAtivoFormData) => {
    await createMutation.mutateAsync({ ...data, tipo_ativo_id: tipoAtivoId });
    form.reset();
    onOpenChange(false);
  };

  const generateNomeCampo = (label: string) => {
    return label
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Novo Campo Customizado</DialogTitle>
          <DialogDescription>
            Adicione um novo campo específico para este tipo de ativo
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Label *</FormLabel>
                  <FormDescription>Nome amigável que aparecerá no formulário</FormDescription>
                  <FormControl>
                    <Input
                      placeholder="Ex: CPU"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        if (!form.getValues('nome_campo')) {
                          form.setValue('nome_campo', generateNomeCampo(e.target.value));
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nome_campo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome do Campo *</FormLabel>
                  <FormDescription>Nome interno do campo (snake_case)</FormDescription>
                  <FormControl>
                    <Input placeholder="Ex: cpu" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="tipo_campo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Campo *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="text">Texto Curto</SelectItem>
                        <SelectItem value="textarea">Texto Longo</SelectItem>
                        <SelectItem value="number">Número</SelectItem>
                        <SelectItem value="date">Data</SelectItem>
                        <SelectItem value="boolean">Sim/Não</SelectItem>
                        <SelectItem value="select">Lista Suspensa</SelectItem>
                        <SelectItem value="multiselect">Múltipla Escolha</SelectItem>
                        <SelectItem value="email">E-mail</SelectItem>
                        <SelectItem value="url">URL</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tab"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tab</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="basico">Básico</SelectItem>
                        <SelectItem value="tecnico">Técnico</SelectItem>
                        <SelectItem value="negocio">Negócio</SelectItem>
                        <SelectItem value="seguranca">Segurança</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="descricao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormDescription>Texto de ajuda que aparecerá abaixo do campo</FormDescription>
                  <FormControl>
                    <Textarea placeholder="Descrição opcional" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="obrigatorio"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel>Obrigatório</FormLabel>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="ordem"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ordem</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="0"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {(tipoCampoSelecionado === 'select' || tipoCampoSelecionado === 'multiselect') && (
              <FormField
                control={form.control}
                name="configuracoes.opcoes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Opções</FormLabel>
                    <FormDescription>Uma opção por linha</FormDescription>
                    <FormControl>
                      <Textarea
                        placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                        value={field.value?.join('\n') || ''}
                        onChange={(e) => {
                          const opcoes = e.target.value.split('\n').filter(o => o.trim());
                          form.setValue('configuracoes', {
                            ...form.getValues('configuracoes'),
                            opcoes,
                          });
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {tipoCampoSelecionado === 'number' && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="configuracoes.sufixo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sufixo</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: GB, %, kg" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="configuracoes.prefixo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prefixo</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: R$, US$" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Criando...' : 'Criar Campo'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
